/* eslint-disable no-nested-ternary */
import React, { memo, useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { useGridCellEditor } from 'ag-grid-react'
import type { CustomCellEditorProps } from 'ag-grid-react'
import { useTranslation } from 'react-i18next'
import { getLocaleSettings, getLocaleFromLanguage } from '../gridFormatUtils'
import './NumericCellEditor.css'

interface PercentageCellEditorParams {
  allowNegative?: boolean
  maxValue?: number
  minValue?: number
  placeholder?: string
  precision?: number // Number of decimal places for storage (default: 4 for percentage = 0.5531)
}

/**
 * Percentage Cell Editor
 *
 * Displays and edits values as percentages (e.g., 15%) but stores them as decimals (0.15)
 * Used for reference-based budgeting where values are percentage changes
 */
const PercentageCellEditor = memo(
  ({
    value,
    onValueChange,
    stopEditing,
    eventKey,
    cellStartedEdit,
    allowNegative = true,
    maxValue,
    minValue,
    placeholder = '',
    precision = 4
  }: CustomCellEditorProps<any, number> & PercentageCellEditorParams) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const [internalValue, setInternalValue] = useState<string>('')
    const [hasError, setHasError] = useState(false)
    const { i18n } = useTranslation()

    // Get locale-specific settings
    const localeSettings = useMemo(() => {
      const locale = getLocaleFromLanguage(i18n.language)
      return getLocaleSettings(locale)
    }, [i18n.language])

    const { decimalSeparator } = localeSettings

    // Initialize the editor value - only run once on mount
    useEffect(() => {
      let startValue: string
      let shouldSelectAll = false

      // Handle backspace key
      if (eventKey === 'Backspace' || eventKey === 'Delete') {
        startValue = ''
        // Immediately notify of null value
        onValueChange(null)
      }
      // Handle numeric key press or decimal separator
      else if (eventKey && new RegExp(`^[0-9\\-${decimalSeparator.replace('.', '\\.')}]$`).test(eventKey)) {
        startValue = eventKey
        shouldSelectAll = false // User is typing, don't select
        // Immediately notify of the initial value
        const numValue = parseFloat(startValue.replace(decimalSeparator, '.'))
        if (!Number.isNaN(numValue)) {
          // Convert percentage to decimal and round to specified precision (15 -> 0.15)
          const decimalValue = numValue / 100
          const roundedValue = Number(decimalValue.toFixed(precision))
          onValueChange(roundedValue)
        }
      }
      // Use existing value (double-click, Enter, F2, etc.)
      else if (value !== undefined && value !== null) {
        // Convert decimal to percentage for display (0.15 -> 15)
        // Round to display precision (precision - 2) to avoid floating point issues
        // Storage: 3 decimals (0.553), Display: 1 decimal (55.3%)
        const displayDecimals = Math.max(0, precision - 2)
        const percentageValue = Number((value * 100).toFixed(displayDecimals))
        startValue = String(percentageValue).replace('.', decimalSeparator)
        shouldSelectAll = true // Not a numeric key, select all for replacement
      } else {
        startValue = ''
        shouldSelectAll = true // Empty cell, select all (though nothing to select)
      }

      setInternalValue(startValue)

      // Focus and optionally select all text only if this cell started the edit
      if (cellStartedEdit) {
        setTimeout(() => {
          inputRef.current?.focus()
          if (shouldSelectAll) {
            inputRef.current?.select()
          }
        }, 0)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []) // Only run on mount

    // Validation logic
    const getValidationErrors = useCallback(() => {
      if (internalValue === '' || internalValue === '-') {
        return null // Allow empty values and negative sign in progress
      }

      // Normalize: replace locale decimal separator with standard period
      const normalizedValue = internalValue.replace(decimalSeparator, '.')
      const numValue = parseFloat(normalizedValue)

      if (Number.isNaN(numValue)) {
        return ['Please enter a valid percentage']
      }

      if (!allowNegative && numValue < 0) {
        return ['Negative percentages are not allowed']
      }

      // Convert to decimal for min/max validation
      const decimalValue = numValue / 100

      if (minValue !== undefined && decimalValue < minValue) {
        return [`Value must be at least ${minValue * 100}%`]
      }

      if (maxValue !== undefined && decimalValue > maxValue) {
        return [`Value must be at most ${maxValue * 100}%`]
      }

      return null
    }, [internalValue, allowNegative, minValue, maxValue, decimalSeparator])

    // Register validation with AG Grid
    useGridCellEditor({
      getValidationErrors
    })

    // Handle input change
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value
        // Allow empty string
        if (val === '') {
          setInternalValue('')
          onValueChange(null)
          setHasError(false)
          return
        }

        // Allow negative sign at the beginning
        if (val === '-' && allowNegative) {
          setInternalValue(val)
          setHasError(false)
          return
        }

        // Create locale-aware regex that accepts the locale's decimal separator
        const escapedSeparator = decimalSeparator.replace('.', '\\.')
        const regex = allowNegative
          ? new RegExp(`^-?\\d*[${escapedSeparator}]?\\d*$`)
          : new RegExp(`^\\d*[${escapedSeparator}]?\\d*$`)

        if (regex.test(val)) {
          setInternalValue(val)

          // Normalize: replace locale decimal separator with standard period for parsing
          const normalizedVal = val.replace(decimalSeparator, '.')

          // Parse and update value
          const numValue = parseFloat(normalizedVal)
          if (!Number.isNaN(numValue)) {
            // Convert percentage to decimal and round to specified precision (15 -> 0.15)
            const decimalValue = numValue / 100
            const roundedValue = Number(decimalValue.toFixed(precision))
            onValueChange(roundedValue)
            const errors = getValidationErrors()
            setHasError(errors !== null)
          } else {
            setHasError(false)
          }
        }
      },
      [onValueChange, allowNegative, getValidationErrors, decimalSeparator, precision]
    )

    // Handle keyboard events
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Stop editing on Enter
        if (e.key === 'Enter') {
          const errors = getValidationErrors()
          if (errors === null) {
            stopEditing()
          }
        }
        // Cancel editing on Escape
        else if (e.key === 'Escape') {
          stopEditing(true) // Cancel = true
        }
        // Allow navigation keys
        else if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Tab'].includes(e.key)) {
          // Let the default behavior happen
        }
        // Stop propagation for other keys to prevent grid shortcuts
        else {
          e.stopPropagation()
        }
      },
      [stopEditing, getValidationErrors]
    )

    return (
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <input
          ref={inputRef}
          type="text"
          className={`numeric-cell-editor ${hasError ? 'error' : ''}`}
          value={internalValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          inputMode="decimal"
          style={{ width: '100%', paddingRight: '20px', textAlign: 'right' }}
        />
        <span
          style={{
            position: 'absolute',
            right: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#999',
            pointerEvents: 'none'
          }}
        >
          %
        </span>
      </div>
    )
  }
)

PercentageCellEditor.displayName = 'PercentageCellEditor'

export default PercentageCellEditor
